import React from 'react';
import { Message } from '../../types/chat';
import './MessageRenderer.css';

interface MessageRendererProps {
  message: Message;
}

const MessageRenderer: React.FC<MessageRendererProps> = ({ message }) => {
  // Función para detectar y renderizar tablas
  const renderTable = (text: string) => {
    const tableRegex = /\|(.+)\|/g;
    const lines = text.split('\n');
    const tableLines = lines.filter(line => line.includes('|'));
    
    if (tableLines.length >= 2) {
      const headers = tableLines[0].split('|').filter(cell => cell.trim());
      const dataRows = tableLines.slice(2).filter(line => line.trim());
      
      return (
        <div className="message-table-container">
          <table className="message-table">
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th key={index}>{header.trim()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, rowIndex) => {
                const cells = row.split('|').filter(cell => cell.trim());
                return (
                  <tr key={rowIndex}>
                    {cells.map((cell, cellIndex) => (
                      <td key={cellIndex}>{cell.trim()}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  // Función para detectar y renderizar listas
  const renderList = (text: string) => {
    const lines = text.split('\n');
    const listItems = lines.filter(line => 
      line.trim().startsWith('- ') || 
      line.trim().startsWith('• ') || 
      line.trim().startsWith('* ') ||
      /^\d+\./.test(line.trim())
    );
    
    if (listItems.length > 0) {
      return (
        <ul className="message-list">
          {listItems.map((item, index) => (
            <li key={index}>
              {item.replace(/^[-•*]\s*/, '').replace(/^\d+\.\s*/, '')}
            </li>
          ))}
        </ul>
      );
    }
    return null;
  };

  // Función para detectar y renderizar cards de información
  const renderInfoCard = (text: string) => {
    const cardRegex = /📋|💡|⚠️|✅|❌|💰|🏗️|🔧/;
    if (cardRegex.test(text)) {
      const icon = text.match(cardRegex)?.[0] || '💡';
      const content = text.replace(cardRegex, '').trim();
      
      return (
        <div className="message-info-card">
          <div className="info-card-icon">{icon}</div>
          <div className="info-card-content">{content}</div>
        </div>
      );
    }
    return null;
  };

  // Función para renderizar texto con formato
  const renderFormattedText = (text: string) => {
    // Detectar **texto en negrita**
    let formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Detectar *texto en cursiva*
    formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Detectar `código`
    formattedText = formattedText.replace(/`(.*?)`/g, '<code>$1</code>');
    
    // Detectar enlaces [texto](url)
    formattedText = formattedText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    return formattedText;
  };

  // Función principal de renderizado
  const renderContent = (text: string) => {
    // Dividir el texto en bloques
    const blocks = text.split('\n\n');
    
    return blocks.map((block, index) => {
      const trimmedBlock = block.trim();
      
      // Intentar renderizar como tabla
      const table = renderTable(trimmedBlock);
      if (table) return <div key={index}>{table}</div>;
      
      // Intentar renderizar como lista
      const list = renderList(trimmedBlock);
      if (list) return <div key={index}>{list}</div>;
      
      // Intentar renderizar como card de información
      const card = renderInfoCard(trimmedBlock);
      if (card) return <div key={index}>{card}</div>;
      
      // Renderizar como texto formateado
      return (
        <div 
          key={index} 
          className="message-text-block"
          dangerouslySetInnerHTML={{ __html: renderFormattedText(trimmedBlock) }}
        />
      );
    });
  };

  return (
    <div className="message-content">
      {renderContent(message.text)}
      <span className="message-time">
        {message.timestamp.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}
      </span>
    </div>
  );
};

export default MessageRenderer;
